import { useMemo, useRef, useState } from 'react';
import { IconCopy, IconEraser } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import {
    TRANSFORM_IDS,
    analyzeText,
    applyTextTransform,
    getReadingTimeMinutes,
} from './textAnalyzerEngine';
import {
    copyTextToClipboard,
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';

const textareaClass = 'min-h-[280px] w-full resize-y rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-3 text-[14px] leading-7 text-[#3D3833] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500';

function transformButtonClass() {
    return 'rounded-lg border border-[#ECE8E4] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#3D3833] transition-colors hover:border-sky-400/50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200';
}

function StatCard({ value, label }) {
    return (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3.5 text-center dark:border-amber-900/40 dark:bg-amber-950/20">
            <p className="text-[22px] font-extrabold leading-none text-amber-600 dark:text-amber-300">
                {value}
            </p>
            <p className="mt-1.5 text-[11px] text-[#6B635C] dark:text-zinc-400">
                {label}
            </p>
        </div>
    );
}

export default function TextAnalyzerText() {
    const { t } = useTranslation();
    const usageRecordedRef = useRef(false);

    const [text, setText] = useState('');

    const stats = useMemo(() => analyzeText(text), [text]);
    const readingTime = useMemo(() => getReadingTimeMinutes(stats.words), [stats.words]);

    const hasInput = text.trim().length > 0;

    const recordUsageOnce = () => {
        if (usageRecordedRef.current) return;
        usageRecordedRef.current = true;
        recordToolUsage('text/text-analyzer', { category: 'text' });
    };

    const handleTextChange = (value) => {
        setText(value);
        if (value.trim()) recordUsageOnce();
    };

    const handleTransform = (transformId) => {
        if (!text.trim()) {
            notifications.show({
                title: t('common.warning'),
                message: t('textAnalyzerText.errors.empty'),
                color: 'yellow',
            });
            return;
        }
        recordUsageOnce();
        setText(applyTextTransform(text, transformId));
    };

    const handleCopy = () => {
        if (!text) return;

        if (copyTextToClipboard(text)) {
            notifications.show({
                title: t('common.success'),
                message: t('textAnalyzerText.copied'),
                color: 'green',
            });
            return;
        }

        void navigator.clipboard?.writeText(text)
            .then(() => {
                notifications.show({
                    title: t('common.success'),
                    message: t('textAnalyzerText.copied'),
                    color: 'green',
                });
            })
            .catch(() => {
                notifications.show({
                    title: t('common.error'),
                    message: t('textAnalyzerText.copyFailed'),
                    color: 'red',
                });
            });
    };

    const handleReset = () => {
        setText('');
        usageRecordedRef.current = false;
    };

    const status = deriveToolStatus({
        hasInput,
        canRun: hasInput,
        hasResult: hasInput,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`textAnalyzerText.howTo.step${i}`)),
        [t],
    );

    const statusText = getToolStatusText(status, t, {
        ready: t('textAnalyzerText.statusReady'),
        success: t('textAnalyzerText.statusSuccess'),
    });

    const readingTimeLabel = readingTime.underOne
        ? t('textAnalyzerText.readingTimeUnderOne')
        : t('textAnalyzerText.readingTimeMinutes', { count: toBnDigits(readingTime.minutes) });

    const statItems = [
        { value: toBnDigits(stats.words), label: t('textAnalyzerText.stats.words') },
        { value: toBnDigits(stats.charsWith), label: t('textAnalyzerText.stats.charsWith') },
        { value: toBnDigits(stats.charsNo), label: t('textAnalyzerText.stats.charsNo') },
        { value: toBnDigits(stats.sentences), label: t('textAnalyzerText.stats.sentences') },
        { value: readingTimeLabel, label: t('textAnalyzerText.stats.readingTime') },
        { value: toBnDigits(stats.unique), label: t('textAnalyzerText.stats.unique') },
    ];

    return (
        <ToolPageLayout
            slug="text/text-analyzer"
            subtitleKey="textAnalyzerText.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('textAnalyzerText.inputTitle')}
            howToSteps={howToSteps}
            onReset={hasInput ? handleReset : undefined}
            belowWorkArea={hasInput ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                    {statItems.map((item) => (
                        <StatCard key={item.label} value={item.value} label={item.label} />
                    ))}
                </div>
            ) : null}
            controls={(
                <ToolCard
                    title={t('textAnalyzerText.textTitle')}
                    headerAction={hasInput ? (
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={handleCopy}
                                className="inline-flex items-center gap-1 rounded-lg border border-[#ECE8E4] px-2 py-1 text-[11.5px] font-semibold text-[#6B635C] transition-colors hover:border-sky-400/50 dark:border-zinc-700 dark:text-zinc-300"
                            >
                                <IconCopy size={13} stroke={1.9} />
                                {t('common.copy')}
                            </button>
                            <button
                                type="button"
                                onClick={handleReset}
                                className="inline-flex items-center gap-1 rounded-lg border border-[#ECE8E4] px-2 py-1 text-[11.5px] font-semibold text-[#6B635C] transition-colors hover:border-sky-400/50 dark:border-zinc-700 dark:text-zinc-300"
                            >
                                <IconEraser size={13} stroke={1.9} />
                                {t('common.reset')}
                            </button>
                        </div>
                    ) : null}
                >
                    <textarea
                        value={text}
                        onChange={(e) => handleTextChange(e.target.value)}
                        placeholder={t('textAnalyzerText.placeholder')}
                        className={textareaClass}
                    />
                    <p className="mt-2 text-right text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                        {t('textAnalyzerText.characterCount', { count: toBnDigits(text.length) })}
                    </p>
                </ToolCard>
            )}
            settings={(
                <ToolCard title={t('textAnalyzerText.transformsTitle')}>
                    <div className="flex flex-wrap gap-2">
                        {TRANSFORM_IDS.map((transformId) => (
                            <button
                                key={transformId}
                                type="button"
                                onClick={() => handleTransform(transformId)}
                                disabled={!hasInput}
                                className={transformButtonClass()}
                            >
                                {t(`textAnalyzerText.transforms.${transformId}`)}
                            </button>
                        ))}
                    </div>
                </ToolCard>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('textAnalyzerText.frequencyTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('textAnalyzerText.previewEmpty')}
                    idleHint={t('textAnalyzerText.previewEmptyHint')}
                    showIdle={!hasInput}
                >
                    {hasInput && stats.topWords.length > 0 ? (
                        <div className="flex h-full min-h-0 flex-col gap-3">
                            <p className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('textAnalyzerText.topWordsTitle', { count: toBnDigits(stats.topWords.length) })}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {stats.topWords.map(([word, count], index) => (
                                    <div
                                        key={word}
                                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${
                                            index === 0
                                                ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30'
                                                : 'border-[#ECE8E4] bg-white dark:border-zinc-700 dark:bg-zinc-900'
                                        }`}
                                    >
                                        <span className={`text-[13px] font-semibold ${index === 0 ? 'text-amber-800 dark:text-amber-300' : 'text-[#3D3833] dark:text-zinc-100'}`}>
                                            {word}
                                        </span>
                                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                                            index === 0
                                                ? 'bg-amber-500 text-white'
                                                : 'bg-[#F7F5F3] text-[#6B635C] dark:bg-zinc-800 dark:text-zinc-300'
                                        }`}
                                        >
                                            {toBnDigits(count)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : hasInput ? (
                        <p className="text-center text-[13px] text-[#A8A099] dark:text-zinc-500">
                            {t('textAnalyzerText.noFrequency')}
                        </p>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
